import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { usePublicCommunity } from './PublicCommunityContext';

export type UserRole = 'user' | 'reviewer' | 'admin' | 'superadmin';
export type CommunityMemberStatus = 'ACTIVE' | 'BLOCKED';

interface CommunityMemberContextType {
  userRole: UserRole;
  isUserInCommunity: boolean;
  isCheckingAccess: boolean;
  error: string | null;
  memberStatus: CommunityMemberStatus | null;
  isAllowedTo: (permission: string, featureKey?: string) => Promise<boolean>;
  joinCommunity: () => Promise<void>;
}

const CommunityMemberContext = createContext<CommunityMemberContextType>({
  userRole: 'user',
  isUserInCommunity: false,
  isCheckingAccess: true,
  error: null,
  memberStatus: null,
  isAllowedTo: async () => false,
  joinCommunity: async () => {}
});

export const useCommunityMember = () => useContext(CommunityMemberContext);

interface CommunityMemberProviderProps {
  children: ReactNode;
}

export const CommunityMemberProvider: React.FC<CommunityMemberProviderProps> = ({ children }) => {
  const { user, loading: authLoading, userProfile } = useAuth();
  const { currentCommunity, isLoading: communityLoading } = usePublicCommunity();
  
  const [userRole, setUserRole] = useState<UserRole>('user');
  const [isUserInCommunity, setIsUserInCommunity] = useState(false);
  const [memberStatus, setMemberStatus] = useState<CommunityMemberStatus | null>(null);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkMembership = async () => {
      setIsCheckingAccess(true);
      setError(null);
      
      try {
        if (!user || !currentCommunity.id) {
          setIsUserInCommunity(false);
          setUserRole('user');
          setMemberStatus(null);
          return;
        }
        
        await Promise.all([
          checkUserMembershipAndStatus(user.id, currentCommunity.id),
          fetchUserRole(user.id, currentCommunity.id)
        ]);
      } catch (error) {
        console.error('[CommunityMemberContext] Error checking membership:', error);
        setError(`Error checking community membership: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        setIsCheckingAccess(false);
      }
    };
    
    if (!authLoading && !communityLoading) {
      console.log('[CommunityMemberContext] Checking membership for user:', user?.id);
      checkMembership();
    }
  }, [user, currentCommunity.id, authLoading, communityLoading]);

  const checkUserMembershipAndStatus = async (userId: string, communityId: string) => {
    try {
      const { data, error } = await supabase
        .from('community_members')
        .select('id, status')
        .eq('community_id', communityId)
        .eq('user_id', userId)
        .maybeSingle();
      
      if (error) {
        console.error('[CommunityMemberContext] Error checking user membership:', error);
        setIsUserInCommunity(false);
        setMemberStatus(null);
        return;
      }
      
      setIsUserInCommunity(!!data);
      setMemberStatus(data?.status || null);
      console.log('[CommunityMemberContext] User in community:', !!data, 'Status:', data?.status);
    } catch (error) {
      console.error('[CommunityMemberContext] Error checking user membership:', error);
      setIsUserInCommunity(false);
      setMemberStatus(null);
    }
  };

  const fetchUserRole = async (userId: string, communityId: string) => {
    try {
      console.log('[CommunityMemberContext] Fetching user role for user:', userId);
      
      const { data, error } = await supabase
        .from('community_members')
        .select('role_name')
        .eq('user_id', userId)
        .eq('community_id', communityId)
        .maybeSingle();
      
      if (error) {
        console.error('[CommunityMemberContext] Error fetching user role:', error);
        setUserRole('user');
      } else if (data) {
        setUserRole((data as { role_name?: UserRole }).role_name || 'user');
        console.log('[CommunityMemberContext] User role:', (data as { role_name?: UserRole }).role_name || 'user');
      } else {
        setUserRole('user');
      }
    } catch (error) {
      console.error('[CommunityMemberContext] Error fetching user role:', error);
      setUserRole('user');
    }
  };

  const isAllowedTo = async (permission: string, featureKey?: string): Promise<boolean> => {
    if (!user) return false;
    if (!currentCommunity?.id) {
      console.error('[CommunityMemberContext] No community context for permission check');
      return false;
    }
    
    try {
      const timeout = 5000; // 5 seconds timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      const { data, error } = await supabase.rpc('is_allowed_to_in_community', {
        user_id: user.id,
        permission_name: permission,
        community_id: currentCommunity.id
      } as {
        user_id: string;
        permission_name: string;
        community_id: string;
      });
      
      clearTimeout(timeoutId);
      
      if (error) {
        console.error('[CommunityMemberContext] Error checking permission:', error);
        return false;
      }
      
      return !!data;
    } catch (error) {
      console.error('[CommunityMemberContext] Error checking permission:', error);
      return false;
    }
  };

  const joinCommunity = async () => {
    if (!user || !currentCommunity?.id || !userProfile) {
      setError("You must be logged in with a complete profile to join this community");
      return;
    }

    setIsCheckingAccess(true);

    try {
      // Check if user was previously blocked
      const { data: existingMember } = await supabase
        .from('community_members')
        .select('status')
        .eq('user_id', user.id)
        .eq('community_id', currentCommunity.id)
        .maybeSingle();

      if (existingMember?.status === 'BLOCKED') {
        throw new Error('You have been blocked from this community. Please contact an administrator for assistance.');
      }

      const { error: rpcError } = await supabase.rpc('add_user_to_community', {
        _user_id: user.id,
        _community_id: currentCommunity.id,
        _firstname: userProfile.firstname || '',
        _lastname: userProfile.lastname || '',
        _gender: userProfile.gender || 'Male',
        _country: userProfile.country || '',
        _town: userProfile.town || '',
        _postal_code: userProfile.postal_code || '',
        _role_name: 'user'
      });

      if (rpcError) throw rpcError;

      await checkUserMembershipAndStatus(user.id, currentCommunity.id);
      await fetchUserRole(user.id, currentCommunity.id);

      console.log('[CommunityMemberContext] Successfully joined community');

    } catch (error) {
      console.error('[CommunityMemberContext] Error joining community:', error);
      setError(error instanceof Error ? error.message : 'Failed to join community');
    } finally {
      setIsCheckingAccess(false);
    }
  };

  return (
    <CommunityMemberContext.Provider
      value={{
        userRole,
        isUserInCommunity,
        isCheckingAccess,
        error,
        memberStatus,
        isAllowedTo,
        joinCommunity
      }}
    >
      {children}
    </CommunityMemberContext.Provider>
  );
};
